/**
 * CLINIC-CATALOG-PRICING-TRUTH-S1-V1 (Refs #315) -- the treatment-catalog
 * BROWSE page view model (pure, React-free; tested by direct call).
 *
 * THE SURFACE: the catalog read-truth browse for the EXISTING catalog
 * audience -- the doctor (the picker's master data) + the leadership tier
 * (master-data provenance). It walks the S1 PROGRESSIVE-DISCLOSURE LAW:
 * clinic scope -> service group -> procedure -> only the selected
 * procedure's price/provenance fields. The full catalog never renders
 * flat; search narrows within the current scope. Groups derive from the
 * catalog's own service_group values (the tariff's grouping column as
 * normalized) -- never hardcoded strings.
 *
 * AUDIENCE (rule 6 of the visibility doctrine -- an IMPLEMENTATION of the
 * existing audience, never an ad-hoc list): the role sets below MIRROR the
 * backend's frozen TREATMENT_CATALOG_READ_ROLES /
 * TREATMENT_CATALOG_ADMIN_ROLES (api/routes/treatment-catalog.ts:71-76;
 * the frontend never imports backend lib -- the repo mirror pattern, e.g.
 * treatment-picker-view.ts money rules). Backend route auth remains the
 * security boundary; this guard is UX cleanliness only (the
 * RequireClinicalWorkbench posture). NO new visibility mechanism, NO
 * flag: clinic-scoped resolution IS the existing tenancy fence (S1
 * ruling) -- a clinic that exists sees its prices; a branch without a
 * clinic record is simply not present.
 *
 * PRICE READ TRUTH: every priced row names its source (Clinic price /
 * Chain rate -- the treatment-picker-view catalogPriceSource vocabulary,
 * REUSED never duplicated); an unpriced service renders the ruled
 * unset-price copy VISIBLY ("Price not set" -- W-B3, founder order
 * 2026-08-01, superseding the 2026-07-27 wording; same state semantics:
 * never a blank, never a crash, never a substituted number).
 */

import type { DashboardMe } from '@/dashboard/types';
import type { TreatmentCatalogItemView } from '@/dashboard/api';
import type { FieldGridField } from '@/dashboard/ui';
import {
  catalogPriceDisplay,
  catalogPriceSource,
  catalogPriceSourceLabel,
  pricingUnitLabel,
} from '@/dashboard/doctor/treatment-picker-view';
import { formatIstDateTime } from '@/lib/datetime-ist';

// --- audience (mirror of the backend frozen sets) ---------------------------

/** Mirror of TREATMENT_CATALOG_READ_ROLES (api/routes/treatment-catalog.ts:71). */
export const TREATMENT_CATALOG_PAGE_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['doctor', 'owner', 'gm_agm', 'regional_head']);

/** Mirror of TREATMENT_CATALOG_ADMIN_ROLES (api/routes/treatment-catalog.ts:75):
 *  the leadership subset that may read INACTIVE rows (provenance). */
export const TREATMENT_CATALOG_PAGE_ADMIN_ROLES: ReadonlySet<DashboardMe['role']> =
  new Set<DashboardMe['role']>(['owner', 'gm_agm', 'regional_head']);

export type CatalogAccessDecision =
  | { readonly kind: 'wait' }
  | { readonly kind: 'redirect'; readonly to: string }
  | { readonly kind: 'render' };

/** The RequireClinicalWorkbench decide idiom, verbatim shape. */
export function decideTreatmentCatalogAccess(state: {
  loading: boolean;
  error: string | null;
  me: DashboardMe | null;
}): CatalogAccessDecision {
  if (state.loading) return { kind: 'wait' };
  if (state.error || !state.me) {
    return { kind: 'redirect', to: '/login' };
  }
  if (!TREATMENT_CATALOG_PAGE_ROLES.has(state.me.role)) {
    return { kind: 'redirect', to: state.me.homePath };
  }
  return { kind: 'render' };
}

/** May this role browse INACTIVE rows (leadership provenance read)? */
export function canBrowseInactive(me: DashboardMe | null): boolean {
  return me !== null && TREATMENT_CATALOG_PAGE_ADMIN_ROLES.has(me.role);
}

// --- clinic scope (disclosure level 0) --------------------------------------

/**
 * The default clinic scope: the operator's global clinic-context selection
 * when set; else the single assigned clinic (the doctor lands on ITS
 * clinic's prices -- role lands on its work); else null = the org-wide
 * provenance view. UI NARROWING WITHIN SERVER-VERIFIED SCOPE only -- the
 * backend re-fences every read (?clinicId scope-honest 403).
 */
export function defaultCatalogScope(
  selectedClinicId: number | null,
  options: ReadonlyArray<{ readonly clinicId: number }>,
): number | null {
  if (selectedClinicId !== null) return selectedClinicId;
  if (options.length === 1) return options[0]?.clinicId ?? null;
  return null;
}

// --- the detail panel (only the SELECTED procedure's fields) ----------------

/**
 * The selected procedure's price/provenance fields (FIELDS, NOT
 * SENTENCES). Price + source ride the SAME reused vocabulary as the
 * picker; effective-from is zone-labeled (IST) per the render law. On the
 * ORG-WIDE (no-clinic) scope an org row's price is labeled as the chain
 * rate and a clinic row is named as that clinic's override (provenance
 * view -- rows ship unresolved with clinicId visible).
 */
export function catalogDetailFields(
  item: TreatmentCatalogItemView,
): readonly FieldGridField[] {
  const source = catalogPriceSource(item);
  return [
    { label: 'Service code', value: item.serviceCode, testId: 'catalog-detail-code' },
    { label: 'Group', value: item.serviceGroup, testId: 'catalog-detail-group' },
    {
      label: 'Pricing unit',
      value: pricingUnitLabel(item.pricingUnit),
      testId: 'catalog-detail-unit',
    },
    {
      label: 'Price',
      value: catalogPriceDisplay(item),
      testId: 'catalog-detail-price',
    },
    {
      label: 'Price source',
      value:
        source === 'not_configured'
          ? // W-B3 unified unset-price vocabulary (founder order 2026-08-01).
            'No price set for this scope'
          : catalogPriceSourceLabel(source),
      testId: 'catalog-detail-source',
    },
    {
      label: 'Status',
      value: item.active ? 'Active' : 'Inactive',
      testId: 'catalog-detail-active',
    },
    {
      label: 'Price effective from',
      value:
        (item.priceEffectiveFrom ?? null) !== null
          ? formatIstDateTime(item.priceEffectiveFrom as string)
          : undefined,
      missingLabel: 'Not recorded',
      testId: 'catalog-detail-effective-from',
    },
  ];
}
